import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export type TugWalkingAid = 'none' | 'cane' | 'walker' | 'other';
export type TugWorkflowStatus = 'awaiting_second_attempt' | 'completed';

export interface TugProtocol {
  chairHeightCm: number;
  distanceMeters: number;
  regularFootwear: boolean;
  customaryAidAllowed: boolean;
  physicalAssistanceAllowed: boolean;
}

export interface TugAttemptPayload {
  completed: boolean;
  timeSeconds: number | null;
  walkingAid: TugWalkingAid;
  walkingAidLabel: string;
  customAidDescription?: string;
  familiarizationPerformed: boolean;
  physicalAssistance: boolean;
  reasonNotCompleted?: string;
  observations?: string;
  finishedAt?: string;
}

export interface TugWorkflowBlock {
  status: TugWorkflowStatus;
  attemptCount: 1 | 2;
  savedAt: string;
}

export interface TugResultPayload {
  workflow?: TugWorkflowBlock;
  attempt1?: TugAttemptPayload;
  attempt2?: TugAttemptPayload;
  completed?: boolean;
  timeSeconds?: number | null;
  walkingAid?: TugWalkingAid;
  walkingAidLabel?: string;
  customAidDescription?: string;
  familiarizationPerformed?: boolean;
  physicalAssistance?: boolean;
  reasonNotCompleted?: string;
  observations?: string;
  interpretation?: string;
  classification?: string;
  referenceComparison?: TugReferenceComparison | null;
  clinicalFlags?: TugClinicalFlag[];
  referenceNote?: string;
  finishedAt?: string;
  protocol?: TugProtocol;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
}

export interface TugSummary {
  attemptCount: 1 | 2;
  workflowStatus: TugWorkflowStatus;
  bestAttempt: 1 | 2 | null;
  bestAttemptPayload: TugAttemptPayload | null;
  bestTimeSeconds: number | null;
  completed: boolean;
  walkingAidLabel: string;
  physicalAssistance: boolean;
  familiarizationPerformed: boolean;
  comparableToReference: boolean;
  referenceComparison: TugReferenceComparison | null;
  referenceStatus: string;
  clinicalFlags: TugClinicalFlag[];
  severity: string;
  severityRange: string;
  interpretation: string;
  referenceNote: string;
}

export interface TugReferenceComparison {
  source: 'Furlanetto2022';
  predictedTimeSeconds: number;
  upperLimitSeconds: number;
  normalLimitPercent: number;
  percentPredicted: number | null;
  ageYears: number;
  ageWithinReferenceRange: boolean;
  heightCm: number;
  bmiKgM2: number;
  withinNormalLimit: boolean | null;
}

export interface TugClinicalFlag {
  key: 'above_reference' | 'reduced_exercise_capacity' | 'fall_risk' | 'very_low_6mwd';
  label: string;
  thresholdLabel: string;
  description: string;
  active: boolean;
}

export const TUG_STANDARD_PROTOCOL: TugProtocol = {
  chairHeightCm: 46,
  distanceMeters: 3,
  regularFootwear: true,
  customaryAidAllowed: true,
  physicalAssistanceAllowed: false,
};

export const TUG_WALKING_AID_OPTIONS: Array<{ value: TugWalkingAid; label: string; description: string }> = [
  { value: 'none', label: 'Sem dispositivo', description: 'Paciente realizou o teste sem auxílio de marcha.' },
  { value: 'cane', label: 'Bengala', description: 'Paciente utilizou bengala habitual durante o percurso.' },
  { value: 'walker', label: 'Andador', description: 'Paciente utilizou andador habitual durante o percurso.' },
  { value: 'other', label: 'Outro', description: 'Registrar outro recurso de marcha utilizado no teste.' },
];

export const TUG_REFERENCE_NORMAL_LIMIT_PERCENT = 121;
export const TUG_REFERENCE_MIN_AGE_YEARS = 20;
export const TUG_REFERENCE_MAX_AGE_YEARS = 80;
export const TUG_COPD_ABNORMAL_THRESHOLD_SECONDS = 11;
export const TUG_COPD_LOW_EXERCISE_CAPACITY_THRESHOLD_SECONDS = 11.2;
export const TUG_COPD_FALL_RISK_THRESHOLD_SECONDS = 12;
export const TUG_COPD_VERY_LOW_6MWD_THRESHOLD_SECONDS = 13.8;
export const TUG_BAR_SCALE_TICKS = [
  { seconds: 0, label: '0 s', position: 0, color: '#10b981' },
  { seconds: TUG_COPD_LOW_EXERCISE_CAPACITY_THRESHOLD_SECONDS, label: '11,2 s', position: 0.25, color: '#06b6d4' },
  { seconds: TUG_COPD_FALL_RISK_THRESHOLD_SECONDS, label: '12 s', position: 0.5, color: '#facc15' },
  { seconds: TUG_COPD_VERY_LOW_6MWD_THRESHOLD_SECONDS, label: '13,8 s', position: 0.75, color: '#fb923c' },
  { seconds: 16, label: '16 s+', position: 1, color: '#f43f5e' },
] as const;
export const TUG_BAR_GRADIENT_CSS = `linear-gradient(90deg, ${TUG_BAR_SCALE_TICKS.map(
  (tick) => `${tick.color} ${tick.position * 100}%`,
).join(', ')})`;

export const TUG_REFERENCE_NOTE =
  'Podsiadlo e Richardson descreveram o TUG como medida prática de mobilidade funcional. Em DPOC, Mesquita et al. observaram pior desempenho que controles, TUG > 11 s como anormal, 11,2 s para identificar maior chance de TC6 < 350 m, 13,8 s para TC6 < 200 m e mudança clinicamente importante de 0,9 a 1,4 s. Al Haddad et al. encontraram TUG ≥ 12 s associado a histórico de quedas. Furlanetto et al. propuseram equação brasileira para adultos de 20 a 80 anos, com limite superior de normalidade em 121% do previsto.';

type TugBand = {
  key:
    | 'not_completed'
    | 'not_comparable'
    | 'within_expected'
    | 'above_reference'
    | 'reduced_exercise_capacity'
    | 'fall_risk'
    | 'very_low_6mwd';
  label: string;
  rangeLabel: string;
  tone: string;
  scoreColor: string;
};

type TugAttemptEntry = {
  index: 1 | 2;
  attempt: TugAttemptPayload;
};

const TUG_BANDS: Record<TugBand['key'], TugBand> = {
  not_completed: {
    key: 'not_completed',
    label: 'Teste não concluído',
    rangeLabel: 'Sem tempo válido',
    tone: 'bg-slate-100 text-slate-700',
    scoreColor: '#475569',
  },
  not_comparable: {
    key: 'not_comparable',
    label: 'Interpretação com cautela',
    rangeLabel: 'Assistência física registrada',
    tone: 'bg-amber-100 text-amber-700',
    scoreColor: '#d97706',
  },
  within_expected: {
    key: 'within_expected',
    label: 'Dentro do esperado',
    rangeLabel: '< 11,2 s e dentro do limite superior de normalidade, quando calculável',
    tone: 'bg-emerald-100 text-emerald-700',
    scoreColor: '#10b981',
  },
  above_reference: {
    key: 'above_reference',
    label: 'Acima do limite de normalidade',
    rangeLabel: '> 11 s ou acima do limite superior de normalidade',
    tone: 'bg-sky-100 text-sky-700',
    scoreColor: '#0284c7',
  },
  reduced_exercise_capacity: {
    key: 'reduced_exercise_capacity',
    label: 'Baixa capacidade funcional provável',
    rangeLabel: '≥ 11,2 s',
    tone: 'bg-amber-100 text-amber-700',
    scoreColor: '#f59e0b',
  },
  fall_risk: {
    key: 'fall_risk',
    label: 'Risco de quedas aumentado',
    rangeLabel: '≥ 12 s',
    tone: 'bg-rose-100 text-rose-700',
    scoreColor: '#f43f5e',
  },
  very_low_6mwd: {
    key: 'very_low_6mwd',
    label: 'Limitação funcional muito importante',
    rangeLabel: '≥ 13,8 s',
    tone: 'bg-red-100 text-red-700',
    scoreColor: '#dc2626',
  },
};

const readNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const readFiniteNumber = (value: unknown): number | null => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseFloat(value.replace(',', '.'))
        : Number.parseFloat(String(value ?? ''));

  return Number.isFinite(parsed) ? parsed : null;
};

const resolveTugWalkingAid = (value: unknown): TugWalkingAid => {
  if (value === 'none' || value === 'cane' || value === 'walker' || value === 'other') return value;
  return 'none';
};

const normalizeWorkflowStatus = (value: unknown): TugWorkflowStatus =>
  value === 'awaiting_second_attempt' ? 'awaiting_second_attempt' : 'completed';

const normalizeTugProtocol = (value: unknown): TugProtocol => {
  if (!value || typeof value !== 'object') return { ...TUG_STANDARD_PROTOCOL };

  const source = value as Partial<TugProtocol>;
  return {
    chairHeightCm: typeof source.chairHeightCm === 'number' ? source.chairHeightCm : TUG_STANDARD_PROTOCOL.chairHeightCm,
    distanceMeters: typeof source.distanceMeters === 'number' ? source.distanceMeters : TUG_STANDARD_PROTOCOL.distanceMeters,
    regularFootwear: source.regularFootwear !== false,
    customaryAidAllowed: source.customaryAidAllowed !== false,
    physicalAssistanceAllowed: Boolean(source.physicalAssistanceAllowed),
  };
};

const normalizeTugAttempt = (value: unknown): TugAttemptPayload | null => {
  if (!value || typeof value !== 'object') return null;

  const source = value as Record<string, unknown>;
  const completed = source.completed !== false;
  const timeSeconds = completed ? normalizeTugTime(source.timeSeconds) : null;
  const walkingAid = resolveTugWalkingAid(source.walkingAid);
  const customAidDescription = readNonEmptyString(source.customAidDescription);
  const walkingAidLabel = getTUGWalkingAidLabel(
    walkingAid,
    customAidDescription ?? readNonEmptyString(source.walkingAidLabel) ?? null,
  );
  const familiarizationPerformed = source.familiarizationPerformed !== false;
  const physicalAssistance = Boolean(source.physicalAssistance);
  const reasonNotCompleted = readNonEmptyString(source.reasonNotCompleted);
  const observations = readNonEmptyString(source.observations);
  const finishedAt = readNonEmptyString(source.finishedAt);
  const hasData =
    'completed' in source ||
    source.timeSeconds != null ||
    'walkingAid' in source ||
    'familiarizationPerformed' in source ||
    'physicalAssistance' in source ||
    reasonNotCompleted != null ||
    observations != null ||
    finishedAt != null;

  if (!hasData) return null;

  return {
    completed,
    timeSeconds,
    walkingAid,
    walkingAidLabel,
    customAidDescription: walkingAid === 'other' ? customAidDescription : undefined,
    familiarizationPerformed,
    physicalAssistance,
    reasonNotCompleted: completed ? undefined : reasonNotCompleted,
    observations,
    finishedAt,
  };
};

const getLegacyAttempt = (payload: unknown): TugAttemptPayload | null => {
  if (!payload || typeof payload !== 'object') return null;
  const source = payload as TugResultPayload;
  if (source.attempt1 || source.attempt2) return null;
  return normalizeTugAttempt(source);
};

export const normalizeTugTime = (value: unknown): number | null => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseFloat(value.replace(',', '.'))
        : Number.parseFloat(String(value ?? ''));

  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Number(parsed.toFixed(1));
};

export const formatTugSeconds = (value?: number | null, decimals = 1): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return Number(value.toFixed(decimals)).toString().replace('.', ',');
};

export const formatTugPercent = (value?: number | null, decimals = 0): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `${Number(value.toFixed(decimals)).toString().replace('.', ',')}%`;
};

export const getTUGWalkingAidLabel = (aid?: TugWalkingAid | null, customAidDescription?: string | null): string => {
  if (aid === 'other') {
    const custom = customAidDescription?.trim();
    return custom && custom.length > 0 ? custom : 'Outro dispositivo';
  }

  return TUG_WALKING_AID_OPTIONS.find((option) => option.value === aid)?.label ?? 'Sem informação';
};

export const calculateTugReferenceComparison = (
  timeSeconds?: number | null,
  patientContext?: Record<string, unknown> | null,
): TugReferenceComparison | null => {
  if (!patientContext || typeof patientContext !== 'object') return null;

  const ageYears = readFiniteNumber(patientContext.ageYears ?? patientContext.age);
  const heightCm = readFiniteNumber(patientContext.heightCm ?? patientContext.height);
  const explicitBmi = readFiniteNumber(patientContext.bmiKgM2 ?? patientContext.bmi);
  const weightKg = readFiniteNumber(patientContext.weightKg ?? patientContext.weight);
  const bmiKgM2 =
    explicitBmi ??
    (weightKg != null && heightCm != null && heightCm > 0 ? weightKg / Math.pow(heightCm / 100, 2) : null);

  if (
    ageYears == null ||
    heightCm == null ||
    bmiKgM2 == null ||
    ageYears < 0 ||
    heightCm <= 0 ||
    bmiKgM2 <= 0
  ) {
    return null;
  }

  const predictedTimeSeconds = Number((8.615 + ageYears * 0.032 + bmiKgM2 * 0.102 - heightCm * 0.026).toFixed(1));
  if (predictedTimeSeconds <= 0) return null;

  const upperLimitSeconds = Number(((predictedTimeSeconds * TUG_REFERENCE_NORMAL_LIMIT_PERCENT) / 100).toFixed(1));
  const normalizedTime = normalizeTugTime(timeSeconds);
  const percentPredicted =
    normalizedTime != null ? Number(((normalizedTime / predictedTimeSeconds) * 100).toFixed(0)) : null;

  return {
    source: 'Furlanetto2022',
    predictedTimeSeconds,
    upperLimitSeconds,
    normalLimitPercent: TUG_REFERENCE_NORMAL_LIMIT_PERCENT,
    percentPredicted,
    ageYears: Number(ageYears.toFixed(0)),
    ageWithinReferenceRange:
      ageYears >= TUG_REFERENCE_MIN_AGE_YEARS && ageYears <= TUG_REFERENCE_MAX_AGE_YEARS,
    heightCm: Number(heightCm.toFixed(1)),
    bmiKgM2: Number(bmiKgM2.toFixed(1)),
    withinNormalLimit: normalizedTime != null ? normalizedTime <= upperLimitSeconds : null,
  };
};

export const getTUGReferenceComparisonLabel = (comparison?: TugReferenceComparison | null): string => {
  if (!comparison || comparison.withinNormalLimit == null) return 'Referência não calculada';
  return comparison.withinNormalLimit
    ? 'Dentro do limite de normalidade'
    : 'Acima do limite de normalidade';
};

export const getTUGReferenceComparisonNote = (
  comparison?: TugReferenceComparison | null,
  patientContext?: Record<string, unknown> | null,
): string => {
  if (!comparison) {
    const ageYears = readFiniteNumber(patientContext?.ageYears ?? patientContext?.age);
    const heightCm = readFiniteNumber(patientContext?.heightCm ?? patientContext?.height);
    const explicitBmi = readFiniteNumber(patientContext?.bmiKgM2 ?? patientContext?.bmi);
    const weightKg = readFiniteNumber(patientContext?.weightKg ?? patientContext?.weight);
    const missing: string[] = [];

    if (ageYears == null || ageYears < 0) missing.push('idade');
    if (heightCm == null || heightCm <= 0) missing.push('altura');
    if ((explicitBmi == null || explicitBmi <= 0) && (weightKg == null || weightKg <= 0)) {
      missing.push('peso aferido nesta avaliação');
    }

    if (!missing.length) {
      return 'Valor previsto não calculado: os dados disponíveis não produziram uma estimativa válida.';
    }

    const missingLabel =
      missing.length === 1
        ? missing[0]
        : `${missing.slice(0, -1).join(', ')} e ${missing[missing.length - 1]}`;
    return `Valor previsto não calculado: falta informar ${missingLabel}.`;
  }

  const resultPercentText =
    comparison.percentPredicted != null
      ? ` Resultado: ${formatTugPercent(comparison.percentPredicted)} do previsto.`
      : '';
  const ageWithinReferenceRange =
    typeof comparison.ageWithinReferenceRange === 'boolean'
      ? comparison.ageWithinReferenceRange
      : comparison.ageYears >= TUG_REFERENCE_MIN_AGE_YEARS && comparison.ageYears <= TUG_REFERENCE_MAX_AGE_YEARS;
  const extrapolationNote = ageWithinReferenceRange
    ? ''
    : ` Estimativa extrapolada: idade de ${formatTugSeconds(comparison.ageYears, 0)} anos fora da faixa de validação da equação (${TUG_REFERENCE_MIN_AGE_YEARS} a ${TUG_REFERENCE_MAX_AGE_YEARS} anos).`;

  return `Tempo previsto: ${formatTugSeconds(comparison.predictedTimeSeconds)} s. Limite superior de normalidade: ${formatTugSeconds(comparison.upperLimitSeconds)} s.${resultPercentText}${extrapolationNote}`;
};

export const getTugAttemptEntries = (payload: unknown): TugAttemptEntry[] => {
  if (!payload || typeof payload !== 'object') return [];

  const source = payload as TugResultPayload;
  const attempt1 = normalizeTugAttempt(source.attempt1);
  const attempt2 = normalizeTugAttempt(source.attempt2);

  if (attempt1 || attempt2) {
    return [
      ...(attempt1 ? [{ index: 1 as const, attempt: attempt1 }] : []),
      ...(attempt2 ? [{ index: 2 as const, attempt: attempt2 }] : []),
    ];
  }

  const legacyAttempt = getLegacyAttempt(source);
  return legacyAttempt ? [{ index: 1, attempt: legacyAttempt }] : [];
};

export const getTugAttempts = (payload: unknown): TugAttemptPayload[] =>
  getTugAttemptEntries(payload).map((entry) => entry.attempt);

export const getTugBestAttempt = (payload: unknown): { attempt: TugAttemptPayload | null; index: 1 | 2 | null } => {
  const entries = getTugAttemptEntries(payload);
  if (!entries.length) return { attempt: null, index: null };

  const timedEntries = entries.filter(
    (entry): entry is TugAttemptEntry & { attempt: TugAttemptPayload & { timeSeconds: number } } =>
      entry.attempt.completed !== false && typeof entry.attempt.timeSeconds === 'number',
  );

  if (timedEntries.length) {
    return timedEntries.reduce((best, entry) => (entry.attempt.timeSeconds < best.attempt.timeSeconds ? entry : best));
  }

  return entries[entries.length - 1] ?? { attempt: null, index: null };
};

const resolveTugBandKey = (
  timeSeconds?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: TugReferenceComparison | null,
): TugBand['key'] => {
  if (!completed || timeSeconds == null) return 'not_completed';
  if (physicalAssistance) return 'not_comparable';
  if (timeSeconds >= TUG_COPD_VERY_LOW_6MWD_THRESHOLD_SECONDS) return 'very_low_6mwd';
  if (timeSeconds >= TUG_COPD_FALL_RISK_THRESHOLD_SECONDS) return 'fall_risk';
  if (timeSeconds >= TUG_COPD_LOW_EXERCISE_CAPACITY_THRESHOLD_SECONDS) return 'reduced_exercise_capacity';
  if (referenceComparison?.withinNormalLimit === false || timeSeconds > TUG_COPD_ABNORMAL_THRESHOLD_SECONDS) {
    return 'above_reference';
  }
  return 'within_expected';
};

export const getTUGSeverityLabel = (
  timeSeconds?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: TugReferenceComparison | null,
): string => TUG_BANDS[resolveTugBandKey(timeSeconds, completed, physicalAssistance, referenceComparison)].label;

export const getTUGSeverityRangeLabel = (
  timeSeconds?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: TugReferenceComparison | null,
): string => TUG_BANDS[resolveTugBandKey(timeSeconds, completed, physicalAssistance, referenceComparison)].rangeLabel;

export const getTUGSeverityTone = (
  timeSeconds?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: TugReferenceComparison | null,
): string => TUG_BANDS[resolveTugBandKey(timeSeconds, completed, physicalAssistance, referenceComparison)].tone;

export const getTUGScoreColor = (
  timeSeconds?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: TugReferenceComparison | null,
): string => TUG_BANDS[resolveTugBandKey(timeSeconds, completed, physicalAssistance, referenceComparison)].scoreColor;

export const getTUGClinicalFlags = (
  timeSeconds?: number | null,
  completed = true,
  physicalAssistance = false,
  referenceComparison?: TugReferenceComparison | null,
): TugClinicalFlag[] => {
  const comparableTimedResult = completed && timeSeconds != null && !physicalAssistance;
  const aboveReference =
    comparableTimedResult &&
    (referenceComparison?.withinNormalLimit === false || timeSeconds > TUG_COPD_ABNORMAL_THRESHOLD_SECONDS);

  return [
    {
      key: 'above_reference',
      label: 'Acima do limite de normalidade',
      thresholdLabel: '> 11 s ou acima de 121% do previsto',
      description: 'Resultado acima do limite observado em controles ou acima do limite superior calculado pela equação de referência.',
      active: Boolean(aboveReference),
    },
    {
      key: 'reduced_exercise_capacity',
      label: 'Baixa capacidade funcional provável',
      thresholdLabel: '≥ 11,2 s',
      description: 'Ponto de corte descrito em DPOC para identificar maior probabilidade de TC6 < 350 m.',
      active: Boolean(
        comparableTimedResult &&
          timeSeconds >= TUG_COPD_LOW_EXERCISE_CAPACITY_THRESHOLD_SECONDS &&
          timeSeconds < TUG_COPD_VERY_LOW_6MWD_THRESHOLD_SECONDS,
      ),
    },
    {
      key: 'fall_risk',
      label: 'Risco de quedas aumentado',
      thresholdLabel: '≥ 12 s',
      description: 'Ponto de corte associado por Al Haddad et al. a histórico de queda no ano anterior em DPOC.',
      active: Boolean(comparableTimedResult && timeSeconds >= TUG_COPD_FALL_RISK_THRESHOLD_SECONDS),
    },
    {
      key: 'very_low_6mwd',
      label: 'Limitação funcional muito importante',
      thresholdLabel: '≥ 13,8 s',
      description: 'Ponto de corte descrito em DPOC para identificar maior probabilidade de TC6 < 200 m.',
      active: Boolean(comparableTimedResult && timeSeconds >= TUG_COPD_VERY_LOW_6MWD_THRESHOLD_SECONDS),
    },
  ];
};

export const isTUGComparableToReference = (
  payload?: Pick<TugAttemptPayload, 'completed' | 'timeSeconds' | 'physicalAssistance' | 'familiarizationPerformed'> | null,
): boolean => {
  if (!payload) return false;

  return Boolean(
    payload.completed !== false &&
      typeof payload.timeSeconds === 'number' &&
      !payload.physicalAssistance &&
      payload.familiarizationPerformed !== false,
  );
};

export const getTUGInterpretationText = (
  payload?: Pick<
    TugAttemptPayload,
    | 'completed'
    | 'timeSeconds'
    | 'walkingAidLabel'
    | 'physicalAssistance'
    | 'familiarizationPerformed'
    | 'reasonNotCompleted'
  > & { attemptCount?: number | null; referenceComparison?: TugReferenceComparison | null } | null,
): string => {
  const attemptCount =
    typeof payload?.attemptCount === 'number' && Number.isFinite(payload.attemptCount) && payload.attemptCount > 1
      ? Math.min(2, Math.round(payload.attemptCount))
      : 1;
  const multiAttemptNote =
    attemptCount > 1 ? ` O melhor tempo entre ${attemptCount} tentativas registradas foi utilizado para a interpretação.` : '';

  if (!payload || payload.completed === false || payload.timeSeconds == null) {
    const reason =
      typeof payload?.reasonNotCompleted === 'string' && payload.reasonNotCompleted.trim().length > 0
        ? ` Motivo registrado: ${payload.reasonNotCompleted.trim()}.`
        : '';
    const prefix =
      attemptCount > 1
        ? `Nenhuma das ${attemptCount} tentativas registradas foi concluída, sugerindo limitação funcional relevante para a sequência levantar-caminhar-girar-sentar.`
        : 'O paciente não concluiu o TUG, sugerindo limitação funcional relevante para a sequência levantar-caminhar-girar-sentar.';

    return `${prefix}${reason}`;
  }

  const timeSeconds = normalizeTugTime(payload.timeSeconds);
  const aidLabel =
    typeof payload.walkingAidLabel === 'string' && payload.walkingAidLabel.trim().length > 0
      ? payload.walkingAidLabel.trim()
      : 'sem informação';
  const normalizedAidLabel = aidLabel.toLocaleLowerCase('pt-BR');
  const walkingAidPhrase =
    normalizedAidLabel === 'sem dispositivo' || normalizedAidLabel === 'nenhum' || normalizedAidLabel === 'nenhuma'
      ? 'sem dispositivo de marcha'
      : normalizedAidLabel === 'sem informação'
        ? 'sem informação sobre dispositivo de marcha'
        : `com uso de ${normalizedAidLabel}`;
  const familiarizationNote =
    payload.familiarizationPerformed === false
      ? ' Não há registro de tentativa de familiarização, o que reduz a comparabilidade com o protocolo original.'
      : '';

  if (payload.physicalAssistance) {
    return `O teste foi concluído em ${formatTugSeconds(timeSeconds)} s, realizado ${walkingAidPhrase} e com necessidade de assistência física. O tempo deve ser interpretado com cautela, pois não é diretamente comparável aos pontos de corte do TUG.${familiarizationNote}${multiAttemptNote}`;
  }

  if (timeSeconds != null && timeSeconds >= TUG_COPD_VERY_LOW_6MWD_THRESHOLD_SECONDS) {
    const thresholdRelation = timeSeconds > TUG_COPD_VERY_LOW_6MWD_THRESHOLD_SECONDS ? 'excede' : 'atinge';
    return `Tempo de ${formatTugSeconds(timeSeconds)} s, realizado ${walkingAidPhrase}. Em DPOC, o resultado ${thresholdRelation} o ponto de corte de 13,8 s associado a maior probabilidade de TC6 < 200 m. Recomenda-se investigação funcional complementar.${familiarizationNote}`;
  }

  if (timeSeconds != null && timeSeconds >= TUG_COPD_FALL_RISK_THRESHOLD_SECONDS) {
    const thresholdRelation = timeSeconds > TUG_COPD_FALL_RISK_THRESHOLD_SECONDS ? 'excede' : 'atinge';
    return `Tempo de ${formatTugSeconds(timeSeconds)} s, realizado ${walkingAidPhrase}. O resultado ${thresholdRelation} o ponto de corte de 12 s associado por Al Haddad et al. a histórico de quedas em DPOC.${familiarizationNote}${multiAttemptNote}`;
  }

  if (timeSeconds != null && timeSeconds >= TUG_COPD_LOW_EXERCISE_CAPACITY_THRESHOLD_SECONDS) {
    const thresholdRelation = timeSeconds > TUG_COPD_LOW_EXERCISE_CAPACITY_THRESHOLD_SECONDS ? 'excede' : 'atinge';
    return `Tempo de ${formatTugSeconds(timeSeconds)} s, realizado ${walkingAidPhrase}. Em DPOC, o resultado ${thresholdRelation} o ponto de corte de 11,2 s descrito para maior probabilidade de TC6 < 350 m; considerar complementar com teste de caminhada quando clinicamente indicado.${familiarizationNote}${multiAttemptNote}`;
  }

  if (payload.referenceComparison?.withinNormalLimit === false || (timeSeconds != null && timeSeconds > TUG_COPD_ABNORMAL_THRESHOLD_SECONDS)) {
    return `Tempo de ${formatTugSeconds(timeSeconds)} s, realizado ${walkingAidPhrase}. O desempenho ficou acima do limite de normalidade disponível, mas abaixo dos pontos de corte respiratórios de 11,2 s, 12 s e 13,8 s; interpretar em conjunto com equilíbrio, marcha, sintomas e contexto clínico.${familiarizationNote}${multiAttemptNote}`;
  }

  return `Tempo de ${formatTugSeconds(timeSeconds)} s, realizado ${walkingAidPhrase}. O desempenho está abaixo dos pontos de corte respiratórios de atenção e, quando a referência pôde ser calculada, dentro do limite superior de normalidade.${familiarizationNote}${multiAttemptNote}`;
};

export const normalizeTugPayload = (payload: unknown): TugResultPayload | null => {
  if (!payload || typeof payload !== 'object') return null;

  const source = payload as TugResultPayload;
  const entries = getTugAttemptEntries(source);
  const attempt1 = entries.find((entry) => entry.index === 1)?.attempt;
  const attempt2 = entries.find((entry) => entry.index === 2)?.attempt;
  const best = getTugBestAttempt(source);
  const bestAttempt = best.attempt ?? attempt1 ?? null;
  const protocol = normalizeTugProtocol(source.protocol);
  const workflowStatus = source.workflow?.status
    ? normalizeWorkflowStatus(source.workflow.status)
    : attempt2
      ? 'completed'
      : 'completed';
  const attemptCount = source.workflow?.attemptCount === 2 || attempt2 ? 2 : 1;
  const referenceNote = readNonEmptyString(source.referenceNote) ?? TUG_REFERENCE_NOTE;
  const completed = bestAttempt ? bestAttempt.completed !== false : false;
  const timeSeconds = bestAttempt?.timeSeconds ?? null;
  const walkingAid = bestAttempt?.walkingAid ?? resolveTugWalkingAid(source.walkingAid);
  const customAidDescription = bestAttempt?.customAidDescription ?? readNonEmptyString(source.customAidDescription);
  const walkingAidLabel = bestAttempt?.walkingAidLabel ?? getTUGWalkingAidLabel(walkingAid, customAidDescription ?? readNonEmptyString(source.walkingAidLabel) ?? null);
  const familiarizationPerformed = bestAttempt?.familiarizationPerformed ?? (source.familiarizationPerformed !== false);
  const physicalAssistance = bestAttempt?.physicalAssistance ?? Boolean(source.physicalAssistance);
  const reasonNotCompleted = bestAttempt?.reasonNotCompleted ?? readNonEmptyString(source.reasonNotCompleted);
  const observations = readNonEmptyString(source.observations) ?? bestAttempt?.observations;
  const finishedAt = readNonEmptyString(source.finishedAt) ?? bestAttempt?.finishedAt;
  const referenceComparison =
    calculateTugReferenceComparison(timeSeconds, source.patientContext) ??
    (source.referenceComparison && typeof source.referenceComparison === 'object'
      ? (source.referenceComparison as TugReferenceComparison)
      : null);
  const clinicalFlags = getTUGClinicalFlags(timeSeconds, completed, physicalAssistance, referenceComparison);
  const interpretation = resolveAssessmentInterpretationText(
    source,
    getTUGInterpretationText({
      completed,
      timeSeconds,
      walkingAidLabel,
      physicalAssistance,
      familiarizationPerformed,
      reasonNotCompleted,
      attemptCount,
      referenceComparison,
    }),
  );
  const classification =
    readNonEmptyString(source.classification) ?? getTUGSeverityLabel(timeSeconds, completed, physicalAssistance, referenceComparison);

  return {
    workflow: {
      status: workflowStatus,
      attemptCount,
      savedAt: readNonEmptyString(source.workflow?.savedAt) ?? finishedAt ?? new Date().toISOString(),
    },
    ...(attempt1 ? { attempt1 } : {}),
    ...(attempt2 ? { attempt2 } : {}),
    completed,
    timeSeconds,
    walkingAid,
    walkingAidLabel,
    customAidDescription: walkingAid === 'other' ? customAidDescription : undefined,
    familiarizationPerformed,
    physicalAssistance,
    reasonNotCompleted: completed ? undefined : reasonNotCompleted,
    observations,
    interpretation,
    classification,
    referenceComparison,
    clinicalFlags,
    referenceNote,
    finishedAt,
    protocol,
    ...(source.patientContext ? { patientContext: source.patientContext } : {}),
    ...(source.professionalContext ? { professionalContext: source.professionalContext } : {}),
  };
};

export const buildTugPayload = (
  attempt1: TugAttemptPayload,
  options?: {
    attempt2?: TugAttemptPayload | null;
    workflowStatus?: TugWorkflowStatus;
    referenceNote?: string;
    protocol?: Partial<TugProtocol>;
    patientContext?: Record<string, unknown>;
    professionalContext?: Record<string, unknown>;
  },
): TugResultPayload => {
  const attempt2 = options?.attempt2 ?? null;
  const basePayload: TugResultPayload = {
    workflow: {
      status: options?.workflowStatus ?? 'completed',
      attemptCount: attempt2 ? 2 : 1,
      savedAt: new Date().toISOString(),
    },
    attempt1,
    ...(attempt2 ? { attempt2 } : {}),
    referenceNote: options?.referenceNote ?? TUG_REFERENCE_NOTE,
    protocol: {
      ...TUG_STANDARD_PROTOCOL,
      ...(options?.protocol ?? {}),
    },
    finishedAt: new Date().toISOString(),
    ...(options?.patientContext ? { patientContext: options.patientContext } : {}),
    ...(options?.professionalContext ? { professionalContext: options.professionalContext } : {}),
  };

  return normalizeTugPayload(basePayload) ?? basePayload;
};

export const isTugWorkflowCompleted = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const normalized = normalizeTugPayload(payload);
  if (!normalized) return false;
  return normalized.workflow?.status === 'completed';
};

export const isTugAwaitingSecondAttempt = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const normalized = normalizeTugPayload(payload);
  if (!normalized) return false;
  return normalized.workflow?.status === 'awaiting_second_attempt';
};

export const summarizeTugResult = (payload: unknown): TugSummary => {
  const normalized = normalizeTugPayload(payload);
  const best = getTugBestAttempt(normalized ?? payload);
  const bestAttempt = best.attempt;
  const attemptCount = normalized?.workflow?.attemptCount ?? (bestAttempt ? 1 : 1);
  const completed = bestAttempt ? bestAttempt.completed !== false : false;
  const bestTimeSeconds = bestAttempt?.timeSeconds ?? null;
  const walkingAidLabel = bestAttempt?.walkingAidLabel ?? normalized?.walkingAidLabel ?? 'Sem informação';
  const physicalAssistance = Boolean(bestAttempt?.physicalAssistance);
  const familiarizationPerformed = bestAttempt ? bestAttempt.familiarizationPerformed !== false : false;
  const comparableToReference = isTUGComparableToReference(bestAttempt);
  const referenceComparison =
    normalized?.referenceComparison ?? calculateTugReferenceComparison(bestTimeSeconds, normalized?.patientContext);
  const clinicalFlags = normalized?.clinicalFlags ?? getTUGClinicalFlags(bestTimeSeconds, completed, physicalAssistance, referenceComparison);
  const severity = getTUGSeverityLabel(bestTimeSeconds, completed, physicalAssistance, referenceComparison);
  const severityRange = getTUGSeverityRangeLabel(bestTimeSeconds, completed, physicalAssistance, referenceComparison);
  const interpretation = resolveAssessmentInterpretationText(
    normalized,
    getTUGInterpretationText({
      completed,
      timeSeconds: bestTimeSeconds,
      walkingAidLabel,
      physicalAssistance,
      familiarizationPerformed,
      reasonNotCompleted: bestAttempt?.reasonNotCompleted,
      attemptCount,
      referenceComparison,
    }),
  );

  return {
    attemptCount,
    workflowStatus: normalized?.workflow?.status ?? 'completed',
    bestAttempt: best.index,
    bestAttemptPayload: bestAttempt,
    bestTimeSeconds,
    completed,
    walkingAidLabel,
    physicalAssistance,
    familiarizationPerformed,
    comparableToReference,
    referenceComparison,
    referenceStatus: getTUGReferenceComparisonLabel(referenceComparison),
    clinicalFlags,
    severity,
    severityRange,
    interpretation,
    referenceNote: normalized?.referenceNote ?? TUG_REFERENCE_NOTE,
  };
};

export const getTUGBarProgress = (timeSeconds?: number | null): number | null => {
  if (typeof timeSeconds !== 'number' || Number.isNaN(timeSeconds)) return null;

  const firstTick = TUG_BAR_SCALE_TICKS[0];
  const lastTick = TUG_BAR_SCALE_TICKS[TUG_BAR_SCALE_TICKS.length - 1];
  const clampedTime = Math.max(firstTick.seconds, Math.min(lastTick.seconds, timeSeconds));

  for (let index = 0; index < TUG_BAR_SCALE_TICKS.length - 1; index += 1) {
    const start = TUG_BAR_SCALE_TICKS[index];
    const end = TUG_BAR_SCALE_TICKS[index + 1];
    if (clampedTime > end.seconds) continue;

    const segmentProgress = (clampedTime - start.seconds) / (end.seconds - start.seconds);
    return start.position + segmentProgress * (end.position - start.position);
  }

  return lastTick.position;
};
